export type AnswerMode = 'single' | 'multiple'

export type SurveyQuestionKind =
  | 'single'
  | 'multiple'
  | 'dropdown'
  | 'text_short'
  | 'text_long'
  | 'matrix'
  | 'scale'
  | 'info'

export type ProductKind = 'test' | 'questionnaire'

export function answerModeFromQuestionType(type: unknown): AnswerMode {
  const n =
    typeof type === 'number'
      ? type
      : typeof type === 'string' && type.trim()
        ? parseInt(type.trim(), 10)
        : NaN
  return n === 1 ? 'multiple' : 'single'
}

export function surveyKindFromQuestionType(type: unknown): SurveyQuestionKind {
  const n =
    typeof type === 'number'
      ? type
      : typeof type === 'string' && type.trim()
        ? parseInt(type.trim(), 10)
        : NaN
  switch (n) {
    case 1:
      return 'multiple'
    case 2:
      return 'dropdown'
    case 3:
      return 'text_short'
    case 4:
      return 'text_long'
    case 5:
      return 'matrix'
    case 6:
      return 'scale'
    case 7:
      return 'info'
    default:
      return 'single'
  }
}

export const SURVEY_QUESTION_TYPE_CODE: Record<SurveyQuestionKind, number> = {
  single: 0,
  multiple: 1,
  dropdown: 2,
  text_short: 3,
  text_long: 4,
  matrix: 5,
  scale: 6,
  info: 7
}

export const SURVEY_QUESTION_KIND_OPTIONS: ReadonlyArray<{
  kind: SurveyQuestionKind
  labelKey: string
}> = [
  { kind: 'single', labelKey: 'modal.test_create.questions.kind_single' },
  { kind: 'multiple', labelKey: 'modal.test_create.questions.kind_multiple' },
  { kind: 'dropdown', labelKey: 'modal.test_create.questions.kind_dropdown' },
  { kind: 'text_short', labelKey: 'modal.test_create.questions.kind_text_short' },
  { kind: 'text_long', labelKey: 'modal.test_create.questions.kind_text_long' },
  { kind: 'matrix', labelKey: 'modal.test_create.questions.kind_matrix' },
  { kind: 'scale', labelKey: 'modal.test_create.questions.kind_scale' },
  { kind: 'info', labelKey: 'modal.test_create.questions.kind_info' }
]

/** UI groups in the «Тип вопроса» dropdown (Figma). */
export type SurveyUiType =
  | 'standard'
  | 'dropdown'
  | 'text'
  | 'info'
  | 'scale'
  | 'matrix'

export const SURVEY_UI_TYPE_OPTIONS: ReadonlyArray<{
  uiType: SurveyUiType
  labelKey: string
}> = [
  { uiType: 'standard', labelKey: 'modal.test_create.questions.kind_standard' },
  { uiType: 'dropdown', labelKey: 'modal.test_create.questions.kind_dropdown' },
  { uiType: 'text', labelKey: 'modal.test_create.questions.kind_text' },
  { uiType: 'info', labelKey: 'modal.test_create.questions.kind_info' },
  { uiType: 'scale', labelKey: 'modal.test_create.questions.kind_scale' },
  { uiType: 'matrix', labelKey: 'modal.test_create.questions.kind_matrix' }
]

export function surveyKindToUiType(kind: SurveyQuestionKind): SurveyUiType {
  if (kind === 'single' || kind === 'multiple') return 'standard'
  if (kind === 'text_short' || kind === 'text_long') return 'text'
  return kind
}

export function buildScaleCols(scaleMax: number): string[] {
  const max = scaleMax >= 10 ? 10 : 5
  return Array.from({ length: max }, (_, i) => String(i + 1))
}

export type AnswerOption = {
  id: string
  text: string
  correct: boolean
  imageFile: File | null
  imageUrl?: string | null
  optionFileContainerId?: string | null
}

export type QuestionDraft = {
  id: string
  text: string
  imageFile: File | null
  imageUrl?: string | null
  questionFileContainerId?: string | null
  answerMode: AnswerMode
  kind?: SurveyQuestionKind
  isRequired?: boolean
  scaleMin?: number
  scaleMax?: number
  scaleMinLabel?: string
  scaleMaxLabel?: string
  matrixRows?: string[]
  matrixCols?: string[]
  options: AnswerOption[]
  explanation: string
  saved: boolean
  serverQuestionId?: string
}

function uid(): string {
  return `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
}

export function createEmptyQuestionDraft(
  kind: SurveyQuestionKind = 'single'
): QuestionDraft {
  const needsOptions =
    kind === 'single' || kind === 'multiple' || kind === 'dropdown'
  return {
    id: uid(),
    text: '',
    explanation: '',
    imageFile: null,
    kind,
    answerMode: kind === 'multiple' ? 'multiple' : 'single',
    options: needsOptions
      ? [
          { id: uid(), text: '', correct: kind === 'single', imageFile: null },
          { id: uid(), text: '', correct: false, imageFile: null }
        ]
      : [],
    isRequired: kind !== 'info',
    scaleMin: 1,
    scaleMax: 5,
    scaleMinLabel: '',
    scaleMaxLabel: '',
    matrixRows: kind === 'matrix' ? ['', '', ''] : undefined,
    matrixCols: kind === 'matrix' ? buildScaleCols(5) : undefined,
    saved: false
  }
}

export function surveyFieldsForUiType(
  uiType: SurveyUiType,
  previousKind: SurveyQuestionKind
): Pick<
  QuestionDraft,
  | 'kind'
  | 'answerMode'
  | 'options'
  | 'scaleMin'
  | 'scaleMax'
  | 'scaleMinLabel'
  | 'scaleMaxLabel'
  | 'matrixRows'
  | 'matrixCols'
  | 'isRequired'
  | 'explanation'
> {
  let kind: SurveyQuestionKind
  if (uiType === 'standard') {
    kind = previousKind === 'multiple' ? 'multiple' : 'single'
  } else if (uiType === 'text') {
    kind = previousKind === 'text_long' ? 'text_long' : 'text_short'
  } else {
    kind = uiType
  }
  const fresh = createEmptyQuestionDraft(kind)
  return {
    kind: fresh.kind,
    answerMode: fresh.answerMode,
    options: fresh.options,
    scaleMin: fresh.scaleMin,
    scaleMax: fresh.scaleMax,
    scaleMinLabel: fresh.scaleMinLabel,
    scaleMaxLabel: fresh.scaleMaxLabel,
    matrixRows: fresh.matrixRows,
    matrixCols: fresh.matrixCols,
    isRequired: fresh.isRequired,
    explanation: ''
  }
}
export type AnswerMode = 'single' | 'multiple'

export type SurveyQuestionKind =
  | 'single'
  | 'multiple'
  | 'dropdown'
  | 'text_short'
  | 'text_long'
  | 'matrix'
  | 'scale'
  | 'info'

export type ProductKind = 'test' | 'questionnaire'

export function answerModeFromQuestionType(type: unknown): AnswerMode {
  const n =
    typeof type === 'number'
      ? type
      : typeof type === 'string' && type.trim()
        ? parseInt(type.trim(), 10)
        : NaN
  return n === 1 ? 'multiple' : 'single'
}

export function surveyKindFromQuestionType(type: unknown): SurveyQuestionKind {
  const n =
    typeof type === 'number'
      ? type
      : typeof type === 'string' && type.trim()
        ? parseInt(type.trim(), 10)
        : NaN
  switch (n) {
    case 1:
      return 'multiple'
    case 2:
      return 'dropdown'
    case 3:
      return 'text_short'
    case 4:
      return 'text_long'
    case 5:
      return 'matrix'
    case 6:
      return 'scale'
    case 7:
      return 'info'
    default:
      return 'single'
  }
}

export const SURVEY_QUESTION_TYPE_CODE: Record<SurveyQuestionKind, number> = {
  single: 0,
  multiple: 1,
  dropdown: 2,
  text_short: 3,
  text_long: 4,
  matrix: 5,
  scale: 6,
  info: 7
}

export const SURVEY_QUESTION_KIND_OPTIONS: ReadonlyArray<{
  kind: SurveyQuestionKind
  labelKey: string
}> = [
  { kind: 'single', labelKey: 'modal.test_create.questions.kind_single' },
  { kind: 'multiple', labelKey: 'modal.test_create.questions.kind_multiple' },
  { kind: 'dropdown', labelKey: 'modal.test_create.questions.kind_dropdown' },
  { kind: 'text_short', labelKey: 'modal.test_create.questions.kind_text_short' },
  { kind: 'text_long', labelKey: 'modal.test_create.questions.kind_text_long' },
  { kind: 'matrix', labelKey: 'modal.test_create.questions.kind_matrix' },
  { kind: 'scale', labelKey: 'modal.test_create.questions.kind_scale' },
  { kind: 'info', labelKey: 'modal.test_create.questions.kind_info' }
]

/** UI groups in the «Тип вопроса» dropdown (Figma). */
export type SurveyUiType =
  | 'standard'
  | 'dropdown'
  | 'text'
  | 'info'
  | 'scale'
  | 'matrix'

export const SURVEY_UI_TYPE_OPTIONS: ReadonlyArray<{
  uiType: SurveyUiType
  labelKey: string
}> = [
  { uiType: 'standard', labelKey: 'modal.test_create.questions.kind_standard' },
  { uiType: 'dropdown', labelKey: 'modal.test_create.questions.kind_dropdown' },
  { uiType: 'text', labelKey: 'modal.test_create.questions.kind_text' },
  { uiType: 'info', labelKey: 'modal.test_create.questions.kind_info' },
  { uiType: 'scale', labelKey: 'modal.test_create.questions.kind_scale' },
  { uiType: 'matrix', labelKey: 'modal.test_create.questions.kind_matrix' }
]

export function surveyKindToUiType(kind: SurveyQuestionKind): SurveyUiType {
  if (kind === 'single' || kind === 'multiple') return 'standard'
  if (kind === 'text_short' || kind === 'text_long') return 'text'
  return kind
}

export function buildScaleCols(scaleMax: number): string[] {
  const max = scaleMax >= 10 ? 10 : 5
  return Array.from({ length: max }, (_, i) => String(i + 1))
}

export type AnswerOption = {
  id: string
  text: string
  correct: boolean
  imageFile: File | null
  imageUrl?: string | null
  optionFileContainerId?: string | null
}

export type QuestionDraft = {
  id: string
  text: string
  imageFile: File | null
  imageUrl?: string | null
  questionFileContainerId?: string | null
  answerMode: AnswerMode
  kind?: SurveyQuestionKind
  isRequired?: boolean
  scaleMin?: number
  scaleMax?: number
  scaleMinLabel?: string
  scaleMaxLabel?: string
  matrixRows?: string[]
  matrixCols?: string[]
  options: AnswerOption[]
  explanation: string
  saved: boolean
  serverQuestionId?: string
}

function uid(): string {
  return `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
}

export function createEmptyQuestionDraft(
  kind: SurveyQuestionKind = 'single'
): QuestionDraft {
  const needsOptions =
    kind === 'single' || kind === 'multiple' || kind === 'dropdown'
  return {
    id: uid(),
    text: '',
    explanation: '',
    imageFile: null,
    kind,
    answerMode: kind === 'multiple' ? 'multiple' : 'single',
    options: needsOptions
      ? [
          { id: uid(), text: '', correct: kind === 'single', imageFile: null },
          { id: uid(), text: '', correct: false, imageFile: null }
        ]
      : [],
    isRequired: kind !== 'info',
    scaleMin: 1,
    scaleMax: 5,
    scaleMinLabel: '',
    scaleMaxLabel: '',
    matrixRows: kind === 'matrix' ? ['', '', ''] : undefined,
    matrixCols: kind === 'matrix' ? buildScaleCols(5) : undefined,
    saved: false
  }
}

export function surveyFieldsForUiType(
  uiType: SurveyUiType,
  previousKind: SurveyQuestionKind
): Pick<
  QuestionDraft,
  | 'kind'
  | 'answerMode'
  | 'options'
  | 'scaleMin'
  | 'scaleMax'
  | 'scaleMinLabel'
  | 'scaleMaxLabel'
  | 'matrixRows'
  | 'matrixCols'
  | 'isRequired'
  | 'explanation'
> {
  let kind: SurveyQuestionKind
  if (uiType === 'standard') {
    kind = previousKind === 'multiple' ? 'multiple' : 'single'
  } else if (uiType === 'text') {
    kind = previousKind === 'text_long' ? 'text_long' : 'text_short'
  } else {
    kind = uiType
  }
  const fresh = createEmptyQuestionDraft(kind)
  return {
    kind: fresh.kind,
    answerMode: fresh.answerMode,
    options: fresh.options,
    scaleMin: fresh.scaleMin,
    scaleMax: fresh.scaleMax,
    scaleMinLabel: fresh.scaleMinLabel,
    scaleMaxLabel: fresh.scaleMaxLabel,
    matrixRows: fresh.matrixRows,
    matrixCols: fresh.matrixCols,
    isRequired: fresh.isRequired,
    explanation: ''
  }
}
